import { useMemo } from 'react';
import { normalizeCompletedStages } from './project-stage';
import { RETIRED_STAGE_IDS } from '../config/brand';

export interface StageNavigationRow {
    stage: number;
    label: string;
    available: boolean;
    completed: boolean;
    current: boolean;
    dataReady: boolean;
    reason: string;
}

export interface AssistantStageNavigationState {
    currentPage: string;
    currentStage: number | string;
    currentProjectId: string | null;
    projectTitle: string;
    projectStatus: string | null;
    readyForBuilderReview: boolean;
    completedStages: number[];
    stages: StageNavigationRow[];
    summary: string;
}

export interface AssistantNavigationStateParams {
    effectiveCompletedStages: Set<number> | any;
    currentProjectId: string | null;
    projects: any[];
    formData: any;
    documents: any[];
    photos: any[];
    videos: any[];
    projectSummary: any;
    analysis: any;
    photoAnalysis: any;
    projectWMS: any;
    scope: any[];
    quoteData: any;
    isAnalyzing: boolean;
    canNavigateToQuoteStage: (stageNumber: number) => boolean;
    stage: number | string;
    currentPage: string;
    quoteTitle: string;
}

export function computeAssistantStageNavigationState({
    effectiveCompletedStages,
    currentProjectId,
    projects = [],
    formData,
    documents = [],
    photos = [],
    videos = [],
    projectSummary,
    scope = [],
    quoteData,
    canNavigateToQuoteStage,
    stage,
    currentPage,
    quoteTitle,
}: AssistantNavigationStateParams): AssistantStageNavigationState {
    const completed = effectiveCompletedStages instanceof Set
        ? effectiveCompletedStages
        : normalizeCompletedStages(effectiveCompletedStages);
    const currentProject = currentProjectId
        ? projects.find(p => p.id === currentProjectId)
        : null;
    const hasStage1Input = !!(
        formData?.notes
        || formData?.address
        || formData?.jobDescription
        || documents.length
        || photos.length
        || videos.length
        || projectSummary
    );
    const hasScope = Array.isArray(scope) && scope.length > 0;
    const hasQuote = !!(quoteData && typeof quoteData === 'object' && Object.keys(quoteData).length > 0);
    const dataReady: Record<number, boolean> = {
        1: true,
        4: hasScope || completed.has(4) || hasQuote,
        5: hasQuote || hasScope || completed.has(5),
    };
    const labels: Record<number, string> = {
        1: 'Stage 1 - Data Entry',
        4: 'Stage 4 - Scope Builder',
        5: 'Stage 5 - Final Quote',
    };
    const readyReasons: Record<number, string> = {
        1: hasStage1Input ? 'project intake is available' : 'new intake can always be opened',
        4: hasScope ? 'scope items exist' : (hasQuote ? 'final quote data exists, so scope can be reopened' : 'Stage 4 is at or behind the current stage'),
        5: hasQuote ? 'final quote data exists' : (hasScope ? 'Stage 4 scope can be viewed as the final quote' : 'Stage 5 is at or behind the current stage'),
    };
    const blockedReasons: Record<number, string> = {
        4: 'scope items are missing',
        5: 'scope items are missing',
    };
    // Stages 2 and 3 are retired (config/brand.js RETIRED_STAGE_IDS): QuoteMaker writes the quote after Stage 1.
    const stageRows: StageNavigationRow[] = [1, 2, 3, 4, 5].filter(n => !RETIRED_STAGE_IDS.includes(n)).map(stageNumber => {
        const available = canNavigateToQuoteStage(stageNumber);
        return {
            stage: stageNumber,
            label: labels[stageNumber] || '',
            available,
            completed: completed.has(stageNumber),
            current: Number(stage) === stageNumber && currentPage === 'quotemaker',
            dataReady: !!dataReady[stageNumber],
            reason: (available ? readyReasons[stageNumber] : (blockedReasons[stageNumber] || '')) || '',
        };
    });
    return {
        currentPage,
        currentStage: stage,
        currentProjectId: currentProjectId || null,
        projectTitle: quoteTitle || currentProject?.title || currentProject?.quoteTitle || '',
        projectStatus: currentProject?.status || null,
        readyForBuilderReview: currentProject?.status === 'ready_for_builder_review' || (completed.has(5) && hasQuote),
        completedStages: Array.from(completed),
        stages: stageRows,
        summary: stageRows.map(row => `${row.stage}:${row.available ? 'available' : 'blocked'}${row.completed ? ':complete' : ''} (${row.reason})`).join('; '),
    };
}

export function useAssistantNavigationState(params: AssistantNavigationStateParams): AssistantStageNavigationState {
    return useMemo(() => computeAssistantStageNavigationState(params), [
        params.effectiveCompletedStages,
        params.currentProjectId,
        params.projects,
        params.formData,
        params.documents,
        params.photos,
        params.videos,
        params.projectSummary,
        params.analysis,
        params.photoAnalysis,
        params.projectWMS,
        params.scope,
        params.quoteData,
        params.isAnalyzing,
        params.canNavigateToQuoteStage,
        params.stage,
        params.currentPage,
        params.quoteTitle,
    ]);
}
